import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { LoggerAssociation } from './interfaces';
import { CrmActivityLoggerAssociationComponent } from './association.component';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CrmActivityLoggerAssociationField } from './association-field.component';
import { Observable, debounceTime, distinctUntilChanged, firstValueFrom, map, of, switchMap } from 'rxjs';
import { ObjectAssociationType } from '../../../../tokens-and-interfaces';
import { SimplifiedOpportunity } from '../../../../tokens-and-interfaces';
import { CompanyTableCustomizationService } from '../../../../shared-services';

@Component({
  selector: 'crm-activity-logger-opportunity-association',
  templateUrl: './opportunity-association.component.html',
  imports: [CommonModule, CrmActivityLoggerAssociationComponent],
  standalone: true,
})
export class CrmActivityLoggerOpportunityAssociationComponent extends CrmActivityLoggerAssociationField<ObjectAssociationType> {
  @Input({ required: true }) set accountGroupId(value: string | null) {
    this.opportunityService?.setAccountGroupId(value ?? '');
  }
  @Input({ required: true }) set associations(value: LoggerAssociation[]) {
    this.setAssociations(value);
  }
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @Input() placeholder = '';

  @Output() associationsChange = new EventEmitter<LoggerAssociation[]>();

  protected readonly customizationService = inject(CompanyTableCustomizationService);
  protected readonly crmObjectType = 'Opportunity';
  protected readonly opportunityService = this.config.services?.opportunityService;

  protected override async fetchObjects(objectIds: string[]): Promise<LoggerAssociation[]> {
    const opportunities = this.opportunityService
      ? await firstValueFrom(this.opportunityService.getMultiOpportunity(objectIds))
      : [];
    return this.mapAssociations(opportunities);
  }

  protected override setupSearchTerm(): Observable<LoggerAssociation[]> {
    return this.searchTerm$$.asObservable().pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((search) => (this.opportunityService ? this.opportunityService.searchOpportunities(search) : of([]))),
      map((filteredOptions) => this.mapAssociations(filteredOptions)),
    );
  }

  private mapAssociations(opportunities: SimplifiedOpportunity[]): LoggerAssociation[] {
    return (
      opportunities?.map(
        (option) =>
          ({
            name: option.name,
            crmObjectId: option.opportunityId,
            crmObjectType: this.crmObjectType,
          }) as LoggerAssociation,
      ) || []
    );
  }

  handleAdd(association: LoggerAssociation): void {
    const associations = this.associationsList();
    if (!associations.find((a) => a.crmObjectId === association.crmObjectId)) {
      const newAssociations = [...associations, association];
      this.associationsList.set(newAssociations);
      this.associationsChange.emit(newAssociations);
    }
  }

  handleRemove(association: LoggerAssociation): void {
    const associations = this.associationsList().filter((a) => a.crmObjectId !== association.crmObjectId);
    this.associationsList.set(associations);
    this.associationsChange.emit(associations);
  }

  handleSearchTermChange(searchTerm?: unknown): void {
    this.updateSearchTerm(searchTerm);
  }
}
